import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import BaseLayout from '../Layout/BaseLayout';
import { useDispatch, useSelector } from 'react-redux';
import {
  prefetchProducts,
  removeAllCompareProducts,
  removeCompareProduct,
  setCameBackFromApply,
  setCameBackFromDetails,
  setCurrentStep,
  setPrefetchProducts,
  setProducts,
  updateFormData,
} from '../features/FormRegistration';
import lottie from 'lottie-web';
import animationData from '../assets/animation.json';
import LeftChevron from '../icons/LeftChevron';
import FilterIcon from '../icons/FilterIcon';
import {
  Checkbox,
  Modal,
  Select,
  Switch,
  Tabs,
  TextInput,
} from '@mantine/core';
import RightChevronButton from '../icons/RightChevron';
import LeftChevronButton from '../icons/LeftChevronButton';
import PrimaryButton from '../components/PrimaryButton';
import CoverComparison from '../components/CoverComparison';
import { useDisclosure } from '@mantine/hooks';

import axiosRequest from '../utils/axiosConfig';
import { CoverIsFor, CoverType } from '../types/types';
import {
  calculateAge,
  calculateAgeBasedOnCAE,
  formatCurrency,
  formatTypeOfCover,
  getAgeFromLoading,
  getExtrasFeatureIcon,
  getHealthFundImagePath,
  getHospitalFeatureIcon,
  getRebate,
  refactorCustomerType,
} from '../utils/helpers';
import { toast } from 'react-toastify';
import CurrentHealthFund from '../components/CurrentHealthFund';
import BottomChevron from '../icons/BottomChevron';
import IsLoadingCoverComparison from '../components/IsLoadingCoverComparison';
import { setComapreProducts, setSelectedProduct } from '../features/Compare';
import GreenCheckIcon from '../icons/GreenCheckIcon';
import RedCrossIcon from '../icons/RedCrossIcon';
import RestrictedIcon from '../icons/RestrictedIcon';
import PreConditionIcon from '../icons/PreConditionIcon';
import TrashIcon from '../icons/TrashIcon';
import ProductComparison from '../Pages/ComparePage';
import StartOver from '../components/modal/Startover';

function Step8() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [showMoreHospital, setShowMoreHospital] = useState(false);
  const [showMoreExtras, setShowMoreExtras] = useState(false);
  const { isDesktopView, isMobileView } = useSelector(
    (state: any) => state.viewport,
  );
  const { selectedProduct, compareProducts: compareHealthFundProducts } =
    useSelector((state: any) => state.compare);

  const {
    formData,
    healthFunds,
    benefits,
    states,
    salesReqBody,
    isBenefitsLoading,
    products,
    prefetchedProducts,
    cameBackFromDetails,
    cameBackFromApply,
    compareProducts,
    currentStep,
  } = useSelector((state: any) => state.formRegistration);
  
  const [pageNumber, setPageNumber] = useState(1);
  const resetPage = () => {
    setPageNumber(1);
  };
  const handlePrev = () => {
    if (pageNumber === 1) return;
    setIsLoading(false);
    setPageNumber((prev) => prev - 1);
  };

  const handleNext = () => {
    if (isLoading) return;
    setPageNumber((prev) => prev + 1);
  };

  const [showCurrentHealthFund, setShowCurrentHealthFund] =
    useState<boolean>(false);

  // Initialize switches based on current form data
  const [hospitalCoverSwitch, setHospitalCoverSwitch] = useState<boolean>(
    formData.typeOfCover === CoverType.Hospital ||
      formData.typeOfCover === CoverType.HospitalAndExtras,
  );

  const [extrasFeaturesSwitch, setExtrasFeaturesSwitch] = useState<boolean>(
    formData.typeOfCover === CoverType.Extras ||
      formData.typeOfCover === CoverType.HospitalAndExtras,
  );

  const [hospitalCoverFeatures, setHospitalCoverFeatures] = useState<any>([]);
  const [extrasCoverFeatures, setExtrasCoverFeatures] = useState<any>([]);
  const [excessSelection, setExcessSelection] = useState('$750');
  const abortControllers = useRef<Set<AbortController>>(new Set());

  const excess = () => {
    if (excessSelection === '$750') {
      return {
        from: 0,
        to: 750,
      };
    } else if (excessSelection === '$500') {
      return {
        from: 0,
        to: 500,
      };
    } else if (excessSelection === '$250') {
      return {
        from: 0,
        to: 250,
      };
    } else {
      return {
        from: 0,
        to: 750,
      };
    }
  };

  const [order, setOrder] = useState<'Price' | 'Popularity'>('Price');

  // Function to determine the current effective cover type based on selected benefits
  const getEffectiveCoverType = () => {
    const hasHospitalBenefits = formData.selectedHospitalBenefits?.length > 0;
    const hasExtrasBenefits = formData.selectedExtrasBenefits?.length > 0;
    
    if (hasHospitalBenefits && hasExtrasBenefits) {
      return CoverType.HospitalAndExtras;
    } else if (hasHospitalBenefits) {
      return CoverType.Hospital;
    } else if (hasExtrasBenefits) {
      return CoverType.Extras;
    } else {
      // Default to original selection if no benefits selected
      return formData.typeOfCover;
    }
  };

  const fetchProducts = async (pageParam: number) => {
    const productOrder = order === 'Price' ? 'price' : 'rank';
    
    // Use the effective cover type based on selected benefits
    const effectiveCoverType = getEffectiveCoverType();
    const productType = (() => {
      switch (effectiveCoverType) {
        case CoverType.Hospital:
          return 'Hospital';
        case CoverType.Extras:
          return 'Extra';
        case CoverType.HospitalAndExtras:
          return 'Hospital + Extra';
        default:
          return 'Hospital + Extra';
      }
    })();

    let number_of_products;

    if (isMobileView) {
      number_of_products = showCurrentHealthFund ? 1 : 2;
    } else {
      if (showFilters) {
        number_of_products = showCurrentHealthFund ? 3 : 4;
      } else {
        number_of_products = showCurrentHealthFund ? 3 : 4;
      }
    }
    const benefitIds = [
      ...formData.selectedHospitalBenefits,
      ...formData.selectedExtrasBenefits,
    ];

    let CDACustomer: number = 0;
    let CAECustomer: number = 0;
    let CDAPartner: number = 0;
    let CAEPartner: number = 0;
    
    let customerAge = calculateAgeBasedOnCAE(formData.dateOfBirth?.[0]);
    let partnerAge = calculateAgeBasedOnCAE(formData.dateOfBirth?.[1]);
    
    if (formData.changedLoadingManuallyCustomer) {
      customerAge = getAgeFromLoading(formData.customerLoading);
    }
    if (formData.changedLoadingManuallyPartner) {
      partnerAge = getAgeFromLoading(formData.partnerLoading);
    }

    // CRITICAL FIX: Apply continuous coverage logic FIRST
    if (formData.hadContinuousCover === true) {
      // No LHC loading - continuous coverage
      CDACustomer = Math.min(customerAge, 30);
      CAECustomer = 30; // Force to 30 to avoid LHC loading
    } else {
      // Apply LHC loading - no continuous coverage
      if (customerAge > 30) {
        CDACustomer = 30;
        CAECustomer = customerAge; // This applies LHC loading
      } else {
        CDACustomer = customerAge;
        CAECustomer = 30;
      }
    }

    // Same logic for partner
    if (formData.dateOfBirth?.[1] !== '-') {
      if (formData.hadContinuousCoverPartner === true) {
        // No LHC loading - continuous coverage
        CDAPartner = Math.min(partnerAge, 30);
        CAEPartner = 30; // Force to 30 to avoid LHC loading
      } else {
        // Apply LHC loading - no continuous coverage
        if (partnerAge > 30) {
          CDAPartner = 30;
          CAEPartner = partnerAge; // This applies LHC loading
        } else {
          CDAPartner = partnerAge;
          CAEPartner = 30;
        }
      }
    }

    // Add continuous coverage flags to the product request body:
    const productReqBody = {
      product_type: productType,
      customer_type: refactorCustomerType(formData.coverIsFor),
      state: formData.state,
      excess: excess(),
      page_number: pageParam,
      number_of_products,
      benefit_ids: benefitIds,
      order: productOrder,
      dependants: salesReqBody.dependants.map((dependant: any) => {
        return {
          age: Math.floor(Number.parseInt(dependant.age) || 0),
          university: dependant.education_details.full_time === 'Yes' ? true : false,
        };
      }),
      start_date: '2026-04-01',
      rebate_percentage: formData.rebatePercentage !== 'No Rebate' ? formData.rebatePercentage : 0,
      customer: {
        certified_discount_age: Math.floor(CDACustomer),
        certified_age_of_entry: Math.floor(CAECustomer),
        continuous_cover: formData.hadContinuousCover, // Add this flag
      },
      ...(formData.dateOfBirth?.[1] !== '-' && 
          ![CoverIsFor.Single, CoverIsFor.SingleParentFamily].includes(formData.coverIsFor) && {
        partner: {
          certified_discount_age: Math.floor(CDAPartner ?? 0),
          certified_age_of_entry: Math.floor(CAEPartner ?? 0),
          continuous_cover: formData.hadContinuousCoverPartner, // Add this flag
        },
      }),
    };
    if (salesReqBody.dependants.length === 0) {
      delete productReqBody.dependants;
    }
    const abortController = new AbortController();
    abortControllers.current.add(abortController);

    try {
      if (currentStep < 10) {
        return;
      }
      setIsLoading(true);
      
      const res = await axiosRequest.post('/products', productReqBody, {
        signal: abortController.signal,
      });

      const productsArray: any = Object.values(res?.data?.data);

      if (productsArray.length === 0) {
        
        return [];
      }

      const productIds = productsArray.map((product: any) => ({
        product_id: product.product_id,
      }));

      const selectedState = states.find(
        (state: any) => state.state_name === formData.location,
      );

      // Create a single price request with all product IDs
      const priceReqBody = {
        state_id: selectedState?.state_id || 1,
        customer_type: refactorCustomerType(formData.coverIsFor),
        start_date: '2026-04-01',
        rebate_percentage:
          formData.rebatePercentage !== 'No Rebate'
            ? formData.rebatePercentage
            : 0,
        customer: {
          certified_discount_age: Math.floor(CDACustomer),
          certified_age_of_entry: Math.floor(CAECustomer),
        },
        ...(formData.dateOfBirth?.[1] !== '-' && 
            ![CoverIsFor.Single, CoverIsFor.SingleParentFamily].includes(formData.coverIsFor) && {
          partner: {
            certified_discount_age: Math.floor(CDAPartner ?? 0),
            certified_age_of_entry: Math.floor(CAEPartner ?? 0),
          },
        }),
        products: productIds,
        dependants: salesReqBody.dependants.map((dependant: any) => ({
          age: Math.floor(Number.parseInt(dependant.age) || 0), // Ensure integer
          university:
            dependant.education_details.full_time === 'Yes' ? true : false,
        })),
      };

      if (salesReqBody.dependants.length === 0) {
        delete priceReqBody.dependants;
      }

      // Make the API calls in parallel
      const [priceRes, benefitRes] = await Promise.all([
        axiosRequest.post('/prices', priceReqBody, {
          signal: abortController.signal,
        }),
        axiosRequest.post(
          '/productBenefits',
          {
            products: productIds,
          },
          { signal: abortController.signal },
        ),
      ]);

      // Process the results and combine with product data
      const paymentFrequency =
        formData.paymentFrequency?.toLowerCase() === 'annual'
          ? 'yearly'
          : formData.paymentFrequency?.toLowerCase();

      const updatedProducts = productsArray.map((product: any) => {
        // Find the matching price data for this product
        const priceData = priceRes.data.data.find(
          (priceItem: any) => priceItem.product_id === product.product_id,
        );

        // Find the matching benefits data for this product
        const benefitData = Object.values(benefitRes.data.data)
          .filter((item: any) => item.product_id === product.product_id)
          .map((item: any) => item.benefits)
          .reduce((acc: any, item: any) => [...acc, ...item], []);

        // Extract and format price information
        const priceValue = priceData?.prices?.[paymentFrequency]?.price;
        const priceParts =
          priceValue !== undefined
            ? priceValue.toFixed(2).toString().split('.')
            : ['0', '00'];

        // Update the product with all the collected data
        return {
          ...product,
          coverMainPrice: priceParts[0],
          coverMainPriceDecimal: priceParts[1] || '00',
          lhcComponent: priceData?.prices?.[paymentFrequency]?.lhc_component,
          productBenefits: benefitData,
        };
      });

      return updatedProducts;
    } catch (error) {
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for updating hospital benefits with automatic cover type switching
  const updateHospitalBenefits = (benefitId: number, isSelected: boolean) => {
    let newSelectedBenefits;
    
    if (isSelected) {
      newSelectedBenefits = formData.selectedHospitalBenefits.filter(
        (id: number) => id !== benefitId,
      );
    } else {
      newSelectedBenefits = [
        ...formData.selectedHospitalBenefits,
        benefitId,
      ];
    }
    
    // Update form data with new benefits
    const updatedFormData = {
      ...formData,
      selectedHospitalBenefits: newSelectedBenefits,
    };
    
    // Determine new cover type based on benefit selections
    const hasHospitalBenefits = newSelectedBenefits.length > 0;
    const hasExtrasBenefits = formData.selectedExtrasBenefits?.length > 0;
    
    let newCoverType = formData.typeOfCover;
    if (hasHospitalBenefits && hasExtrasBenefits) {
      newCoverType = CoverType.HospitalAndExtras;
    } else if (hasHospitalBenefits && !hasExtrasBenefits) {
      newCoverType = CoverType.Hospital;
    } else if (!hasHospitalBenefits && hasExtrasBenefits) {
      newCoverType = CoverType.Extras;
    }
    
    // Update switches to match new cover type
    setHospitalCoverSwitch(
      newCoverType === CoverType.Hospital || newCoverType === CoverType.HospitalAndExtras
    );
    setExtrasFeaturesSwitch(
      newCoverType === CoverType.Extras || newCoverType === CoverType.HospitalAndExtras
    );
    
    dispatch(
      updateFormData({
        ...updatedFormData,
        typeOfCover: newCoverType,
      }),
    );
  };

  // Wrapper for Checkbox onChange
  const handleHospitalBenefitChange = (benefitId: number) => {
    const isSelected = formData.selectedHospitalBenefits.includes(benefitId);
    updateHospitalBenefits(benefitId, isSelected);
  };

  // Wrapper for Checkbox onChange for Extras
  const handleExtrasBenefitChange = (benefitId: number) => {
    const isSelected = formData.selectedExtrasBenefits.includes(benefitId);
    updateExtrasBenefits(benefitId, isSelected);
  };

  // Handler for updating extras benefits with automatic cover type switching
  const updateExtrasBenefits = (benefitId: number, isSelected: boolean) => {
    let newSelectedBenefits;
    
    if (isSelected) {
      newSelectedBenefits = formData.selectedExtrasBenefits.filter(
        (feature: any) => feature !== benefitId,
      );
    } else {
      newSelectedBenefits = [
        ...formData.selectedExtrasBenefits,
        benefitId,
      ];
    }
    
    // Update form data with new benefits
    const updatedFormData = {
      ...formData,
      selectedExtrasBenefits: newSelectedBenefits,
    };
    
    // Determine new cover type based on benefit selections
    const hasHospitalBenefits = formData.selectedHospitalBenefits?.length > 0;
    const hasExtrasBenefits = newSelectedBenefits.length > 0;
    
    let newCoverType = formData.typeOfCover;
    if (hasHospitalBenefits && hasExtrasBenefits) {
      newCoverType = CoverType.HospitalAndExtras;
    } else if (hasHospitalBenefits && !hasExtrasBenefits) {
      newCoverType = CoverType.Hospital;
    } else if (!hasHospitalBenefits && hasExtrasBenefits) {
      newCoverType = CoverType.Extras;
    }
    
    // Update switches to match new cover type
    setHospitalCoverSwitch(
      newCoverType === CoverType.Hospital || newCoverType === CoverType.HospitalAndExtras
    );
    setExtrasFeaturesSwitch(
      newCoverType === CoverType.Extras || newCoverType === CoverType.HospitalAndExtras
    );
    
    dispatch(
      updateFormData({
        ...updatedFormData,
        typeOfCover: newCoverType,
      }),
    );
  };

  const prefetchMissingPages = async () => {
    const prefetchPage = async (pageParam: number) => {
      if (!prefetchedProducts[pageParam]) {
        try {
          const products = await fetchProducts(pageParam);
          dispatch(prefetchProducts({ [pageParam]: products }));
        } catch (error) {
        }
      }
    };

    const nextPages = [pageNumber + 1, pageNumber + 2, pageNumber + 3];

    nextPages.forEach((page) => {
      prefetchPage(page);
    });
  };

  const loadInitialData = async () => {
    setIsLoading(true); // Start loading only for the current page
    try {
      const initialProducts = await fetchProducts(pageNumber);
      dispatch(setProducts(initialProducts));
      dispatch(prefetchProducts({ [pageNumber]: initialProducts }));
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveFilters = async () => {
    resetPage();
    dispatch(setProducts([]));
    dispatch(setPrefetchProducts([]));
    abortControllers.current.forEach((controller) => controller.abort());
    abortControllers.current.clear();

    await loadInitialData();
    await prefetchMissingPages();
  };

  useEffect(() => {
    const resetPageOrder = async () => {
      resetPage();
      dispatch(setProducts([]));
      dispatch(setPrefetchProducts([]));
      abortControllers.current.forEach((controller) => controller.abort());
      abortControllers.current.clear();

      await loadInitialData();
      await prefetchMissingPages();
    };

    resetPageOrder();
  }, [order]);

  useEffect(() => {
    if (prefetchedProducts && prefetchedProducts[pageNumber]) {
      if (prefetchedProducts[pageNumber].length > 0) {
        dispatch(setProducts(prefetchedProducts[pageNumber]));
      } else {
        loadInitialData();
      }
    } else {
      loadInitialData();
    }

    prefetchMissingPages();
  }, [pageNumber, isDesktopView, isMobileView]);
  
  const [expandedBenefits, setExpandedBenefits] = useState<
    Record<string, boolean>
  >({});

  const handleToggleDescription = (benefitName: string) => {
    setExpandedBenefits((prevState) => ({
      ...prevState,
      [benefitName]: !prevState[benefitName],
    }));
  };
  const container = useRef<HTMLDivElement | null>(null);

  // Remove the old useEffect that was managing typeOfCover based on switches
  // This is now handled in the benefit update functions

  useEffect(() => {
    if (cameBackFromDetails) {
      dispatch(setCameBackFromDetails(false));
      return;
    }
    if (cameBackFromApply) {
      dispatch(setCameBackFromApply(false));
      return;
    }
    // handleSaveFilters();
  }, [cameBackFromApply, cameBackFromDetails]);

  useEffect(() => {
    const hospitalCoverFeaturesTemp: any = [];
    const extrasCoverFeaturesTemp: any = [];
    for (let i = 0; i < benefits.length; i++) {
      if (benefits[i].benefit_type === 'hospital') {
        hospitalCoverFeaturesTemp.push(benefits[i]);
      } else {
        extrasCoverFeaturesTemp.push(benefits[i]);
      }
    }
    setHospitalCoverFeatures(hospitalCoverFeaturesTemp);
    setExtrasCoverFeatures(extrasCoverFeaturesTemp);
  }, [benefits]);

  
  // Auto-refresh products when benefits change (desktop only)
  useEffect(() => {
    if (isDesktopView) {
      const timeoutId = setTimeout(() => {
        handleSaveFilters();
      }, 300); // Small delay to prevent too many API calls
      
      return () => clearTimeout(timeoutId);
    }
  }, [
    formData.selectedExtrasBenefits,
    formData.selectedHospitalBenefits,
    excessSelection,
    isDesktopView,
  ]);
  useEffect(() => {
    fetchMyFundProducts();
  }, [formData.healthFund]);

  const fetchMyFundProducts = () => {
    if (!formData.healthFund) return;

    // ✅ FIX: Add comprehensive null safety checks
    if (!salesReqBody) {
      
      return;
    }

    if (!salesReqBody.customer) {
      return;
    }

    let CDACustomer;
    let CAECustomer;
    let CDAPartner;
    let CAEPartner;
    
    let customerAge = calculateAgeBasedOnCAE(formData.dateOfBirth?.[0]);
    let partnerAge = calculateAgeBasedOnCAE(formData.dateOfBirth?.[1]);
    
    if (formData.changedLoadingManuallyCustomer) {
      customerAge = getAgeFromLoading(formData.customerLoading);
    }
    if (formData.changedLoadingManuallyPartner) {
      partnerAge = getAgeFromLoading(formData.partnerLoading);
    }

    // Apply the same continuous coverage logic as fetchProducts
    if (formData.hadContinuousCover === true) {
      CDACustomer = Math.min(customerAge, 30);
      CAECustomer = 30;
    } else {
      if (customerAge > 30) {
        CDACustomer = 30;
        CAECustomer = customerAge;
      } else {
        CDACustomer = customerAge;
        CAECustomer = 30;
      }
    }

    if (formData.dateOfBirth?.[1] !== '-') {
      if (formData.hadContinuousCoverPartner === true) {
        CDAPartner = Math.min(partnerAge, 30);
        CAEPartner = 30;
      } else {
        if (partnerAge > 30) {
          CDAPartner = 30;
          CAEPartner = partnerAge;
        } else {
          CDAPartner = partnerAge;
          CAEPartner = 30;
        }
      }
    }

    // ✅ FIX: Completely remove salesReqBody.dob usage and use formData instead
    let rebate_percentage = 0;
    try {
      if (formData.tierIndex !== undefined && formData.dateOfBirth) {
        // Use formData.dateOfBirth directly - no salesReqBody.dob access
        const dobArray = formData.dateOfBirth.filter((dob: string) => 
          dob && dob !== '-' && dob !== null && dob !== undefined
        );
        
        if (dobArray.length > 0) {
          const rebateString = getRebate(dobArray, formData.tierIndex);
          if (rebateString && typeof rebateString === 'string' && rebateString.includes('%')) {
            const rebateValue = rebateString.split('%')[0];
            if (rebateValue && !isNaN(Number(rebateValue))) {
              rebate_percentage = Number(
                (Number.parseFloat(rebateValue) / 100).toFixed(5)
              ) || 0;
            }
          }
        }
      }
      
      // Fallback to existing rebate percentage
      if (rebate_percentage === 0 && formData.rebatePercentage) {
        rebate_percentage = formData.rebatePercentage;
      }
    } catch (error) {
      rebate_percentage = formData.rebatePercentage || 0;
    }

    const healthFundId = Number.parseInt(formData.healthFund?.split('.')[1]);
    if (!healthFundId || healthFundId === 0) return;
      
    const reqBody = {
      health_fund_id: healthFundId,
      number_of_products: 50,
      page_number: 1,
      start_date: '2026-04-01',
      product_type: formatTypeOfCover(getEffectiveCoverType()),
      state: formData?.state || 1,
      customer_type: refactorCustomerType(formData.coverIsFor),
      rebate_percentage: rebate_percentage,
      customer: {
        certified_discount_age: Math.floor(CDACustomer),
        certified_age_of_entry: Math.floor(CAECustomer),
        continuous_cover: formData.hadContinuousCover,
      },
      // ✅ FIX: Only add partner if it's not Single cover AND partner data exists
      ...(formData.coverIsFor !== CoverIsFor.Single && 
          formData.dateOfBirth?.[1] && 
          formData.dateOfBirth?.[1] !== '-' && {
        partner: {
          certified_discount_age: Math.floor(CDAPartner ?? 0),
          certified_age_of_entry: Math.floor(CAEPartner ?? 0),
          continuous_cover: formData.hadContinuousCoverPartner,
        },
      }),
      price_customer: Number(formData.previousPayment) || 0,
    };

    axiosRequest
      .post('/customerProducts', reqBody)
      .then((res) => {
        const responseData = res.data.data;
        if (responseData) {
          const newProducts = Object.values(responseData);
          dispatch(setComapreProducts(newProducts));
        }
      })
      .catch((err) => {
        console.error('Error fetching customer products:', err);
        // toast.error("Error fetching customer's products");
      });
  };
  const [compareOpened, { open: openComapre, close: closeCompare }] =
    useDisclosure(false);

  const [expandUserDetails, setExpandUserDetails] = useState(false);
  const [expandLegend, setExpandLegend] = useState(false);

  const [expandExtras, setExpandExtras] = useState(true);
  const [expandHospital, setExpandHospital] = useState(true);

  const [showFilters, setShowFilters] = useState(true);
  useEffect(() => {
    handleSaveFilters();
  }, [formData.paymentFrequency, showCurrentHealthFund, showFilters]);

  // ADD: Viewport transition state
  const [isViewportTransitioning, setIsViewportTransitioning] = useState(false);
  
  // ADD: Memoized viewport type
  const viewportType = useMemo(() => {
    if (isMobileView) return 'mobile';
    if (isDesktopView) return 'desktop';
    return 'unknown';
  }, [isMobileView, isDesktopView]);
  
  // ADD: Handle viewport changes with transition state
  useEffect(() => {
    setIsViewportTransitioning(true);
    const timer = setTimeout(() => {
      setIsViewportTransitioning(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [viewportType]);
  
  // ADD: Close modals when viewport changes
  useEffect(() => {
    close();
    closeCompare();
  }, [viewportType]);
  
  // ADD: Early return for viewport transitions
  if (isViewportTransitioning) {
    return (
      <BaseLayout>
        <div className="h-full w-full bg-[white] flex items-center justify-center">
          <div ref={container} className="w-[100px] h-[100px]" />
        </div>
      </BaseLayout>
    );
  }

  if (isMobileView) {
    return (
      <BaseLayout>
        <div key="mobile-step8" className="h-full w-full bg-[white]">
          <div className="px-[17px] py-[8px] flex items-center justify-between">
            <div
              onClick={() => {
                dispatch(setCurrentStep(8));
              }}
              className="cursor-pointer flex items-center w-min">
              <LeftChevron />
              <span className="text-[12px] font-medium">Back</span>
            </div>
            <StartOver />
          </div>
          <div className="px-[17px] pb-[8px]">
            <p className="text-[11px] font-bold text-[#17385D] text-center leading-relaxed">
              Trusted for 10+ Years &nbsp;|&nbsp; No Re-Serving Wait Periods &nbsp;|&nbsp; 40,000+ Aussies Found a Better Deal
            </p>
          </div>
          <div className="px-[15px] md:px-[55px] pb-[20px] flex flex-col">

            {/* Consolidated dark blue filter bar - mobile */}
            <div className="!bg-[#17385D] rounded-[12px] px-[12px] py-[10px] flex flex-col gap-[8px] mb-[8px]">
              {/* Row 1: Filter button + Compare toggle */}
              <div className="flex items-center justify-between">
                <div
                  onClick={open}
                  className="flex cursor-pointer items-center font-bold justify-center gap-[6px] h-[36px] rounded-[100px] px-[14px] !bg-[#FFFFFF]">
                  <span className="text-[13px] font-medium !text-[#17385D]">Filter</span>
                  <FilterIcon fillColor="#17385D" />
                </div>

                <div
                  className="flex items-center gap-[6px] rounded-[8px] px-[10px] py-[6px] !bg-[#FFFFFF]">
                  <span className="text-[12px] font-medium !text-[#17385D]">Compare policy</span>
                  <Switch
                    checked={showCurrentHealthFund}
                    onChange={(event) =>
                      setShowCurrentHealthFund(event.currentTarget.checked)
                    }
                    color="dark"
                    size="xs"
                  />
                </div>
              </div>

              {/* Row 2: Order by + Hospital excess */}
              <div className="flex items-center gap-[8px]">
                <div className="flex-1 flex items-center gap-[6px]">
                  <span className="text-[12px] font-medium whitespace-nowrap !text-[#FFFFFF]">Order by</span>
                  <Select
                    className="flex-1"
                    value={order}
                    data={['Price', 'Popularity']}
                    size="xs"
                    onChange={(value) => {
                      setOrder(value as 'Price' | 'Popularity');
                    }}
                    styles={{ input: { backgroundColor: '#FFFFFF', fontSize: '12px', color: '#17385D' } }}
                  />
                </div>
                <div className="flex-1 flex items-center gap-[6px]">
                  <span className="text-[12px] font-medium whitespace-nowrap !text-[#FFFFFF]">Excess</span>
                  <Select
                    value={excessSelection}
                    data={['$750', '$500', '$250']}
                    size="xs"
                    onChange={(value: any) => {
                      if (!value) return;
                      let excess = {
                        from: 0,
                        to: 750,
                      };
                      if (value === '$750') {
                        excess = {
                          from: 750,
                          to: 750,
                        };
                      } else if (value === '$500') {
                        excess = {
                          from: 500,
                          to: 500,
                        };
                      } else if (value === '$250') {
                        excess = {
                          from: 250,
                          to: 250,
                        };
                      } else {
                        excess = {
                          from: 0,
                          to: 750,
                        };
                      }
                      setExcessSelection(value);
                      handleSaveFilters();
                    }}
                    className="flex-1"
                    styles={{ input: { backgroundColor: '#FFFFFF', fontSize: '12px', color: '#17385D' } }}
                  />
                </div>
              </div>

              {/* Row 3: Info bar */}
              <div
                className="flex items-center justify-between rounded-[8px] px-[10px] py-[6px] !bg-[#FFFFFF]">
                <span className="text-[12px] !text-[#17385D]">
                  Based on your info
                </span>
                <div className="flex items-center gap-[8px]">
                  <div
                    className="flex items-center gap-[3px] cursor-pointer"
                    onClick={() => {
                      setExpandUserDetails((prev) => !expandUserDetails);
                    }}>
                    <span className="text-[12px] font-bold underline !text-[#17385D]">
                      {expandUserDetails ? 'Hide' : 'View'}
                    </span>
                    <BottomChevron
                      className={`transition-transform duration-300 ${
                        expandUserDetails ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                  <span
                    className="text-[12px] cursor-pointer !text-[#D74F58] underline"
                    onClick={() => {
                      dispatch(setCurrentStep(8));
                    }}>
                    Change
                  </span>
                </div>
              </div>
            </div>

            {/* Expanded user details - mobile */}
            <div
              className={`text-[11px] text-start transition-all duration-300 ease-in-out overflow-hidden ${expandUserDetails ? 'max-h-[100px] opacity-100 py-[6px]' : 'max-h-0 opacity-0'}`}>
              <p>
                <strong>{formData.coverIsFor}</strong> cover in{' '}
                <strong>{formData.location}</strong>, I'm{' '}
                <strong>{calculateAge(formData.dateOfBirth[0])}</strong> years
                old,{' '}
                {[CoverIsFor.Couple, CoverIsFor.Family].includes(
                  formData.coverIsFor,
                ) && formData.dateOfBirth?.[1] && formData.dateOfBirth[1] !== '-' && (
                  <span>
                    my partner is{' '}
                    <strong>{calculateAge(formData.dateOfBirth[1])}</strong>
                    years old,{' '}
                  </span>
                )}
                {salesReqBody.dependants.length > 0 &&
                  [CoverIsFor.Family, CoverIsFor.SingleParentFamily].includes(
                    formData.coverIsFor,
                  ) && (
                    <span>
                      <strong>{salesReqBody.dependants.length}</strong> child.
                    </span>
                  )}
                <strong>
                  {(formData.rebatePercentage * 100).toFixed(2)}%
                </strong>{' '}
                Government Rebate. <strong>{formData.customerLoading}</strong>
                % loading.
              </p>
            </div>

            {/* Compare current policy expanded - mobile */}
            <div
              className={`gap-[10px] bg-white text-[12px] w-full flex flex-col text-start transition-all duration-300 ease-in-out overflow-hidden rounded-[8px] border border-[#D1D7DF] ${showCurrentHealthFund ? 'max-h-auto opacity-100 p-[10px] mt-[6px]' : 'max-h-0 opacity-0 border-0 p-0'}`}>
              <span className="text-[12px] text-start">
                Select the provider and the policy to compare
              </span>
              <Select
                label="Select your health fund"
                withAsterisk
                placeholder="Select from the dropdown list"
                withCheckIcon={false}
                className="w-full text-start"
                classNames={{
                  wrapper: '!h-[40px]',
                }}
                rightSection={<BottomChevron />}
                value={formData.healthFund}
                onChange={(value) => {
                  dispatch(
                    updateFormData({
                      ...formData,
                      healthFund: value,
                      hasHealthFund: true,
                    }),
                  );
                  
                  dispatch(setSelectedProduct(null));
                }}
                data={healthFunds}
              />
              <TextInput
                label="Select a price range"
                value={formData.previousPayment}
                onChange={(e) => {
                  dispatch(
                    updateFormData({
                      ...formData,
                      previousPayment: e.target.value,
                    }),
                  );
                }}
                withAsterisk
                placeholder="Select a price range"
                className="w-full text-start"
                classNames={{
                  wrapper: '!h-[40px]',
                  input: '!h-[40px]',
                }}
              />
              <Select
                label="Select a policy"
                placeholder="Select a policy"
                withAsterisk
                withCheckIcon={false}
                className="w-full text-start"
                classNames={{
                  wrapper: '!h-[40px]',
                }}
                rightSection={<BottomChevron />}
                data={compareHealthFundProducts.map((product: any) => ({
                  label: product.product_name,
                  value: product.product_id.toString(),
                }))}
                value={
                  selectedProduct?.product_id.toString() ||
                  compareHealthFundProducts.map((product: any) => ({
                    label: product.product_name,
                    value: product.product_id.toString(),
                  }))[0]?.value
                }
                onChange={(value, option) => {
                  dispatch(
                    setSelectedProduct(
                      compareHealthFundProducts.find(
                        (p: any) => p.product_id == value,
                      ),
                    ),
                  );
                }}
              />
            </div>

            <div
              ref={container}
              className={`w-[100px] h-[100px] self-center  ${
                isLoading ? 'block' : 'hidden'
              }`}
            />

            {isLoading || isBenefitsLoading ? (
              <div className="self-center w-full items-center flex justify-center flex-col gap-[10px]">
                <span>
                  We're finding you a great Health Deal. Results are currently
                  loading, please wait up to 30 seconds.
                </span>

                <div className="flex w-full gap-[5px]">
                  <IsLoadingCoverComparison />
                  <IsLoadingCoverComparison />
                </div>
              </div>
            ) : (
              <div className="flex w-full relative gap-[5px] justify-between items-start">
                {products && products.length > 0 && (
                  <div
                    className={`rounded-[6px]  z-[100] absolute top-[60px] left-[-10px] p-[6px] ${
                      pageNumber === 1
                        ? 'cursor-not-allowed bg-[#D1D7DF]'
                        : 'cursor-pointer bg-[#17385D]'
                    }`}
                    onClick={handlePrev}>
                    <LeftChevronButton fillColor="white" />
                  </div>
                )}
                {products && products.length > 0 && (
                  <div
                    className="rounded-[6px] bg-[#17385D] z-[100] absolute top-[80px] right-[-10px] p-[6px] cursor-pointer"
                    onClick={handleNext}>
                    <RightChevronButton fillColor="white" />
                  </div>
                )}
                {products && products.length === 0 && (
                  <div className="flex items-center  justify-center w-full">
                    <span className="text-[16px] font-medium">
                      There are no items matching your filters
                    </span>
                  </div>
                )}
                <div className="flex w-full gap-[5px]">
                  {' '}
                  {products &&
                    products.map((product: any, index: number) => {
                      if (index > 1) return null;

                      const customerLoading = Number.parseInt(
                        formData.customerLoading,
                      );
                      const partnerLoading = Number.parseInt(
                        formData.partnerLoading,
                      );
                      const loading =
                        formData.coverIsFor === CoverIsFor.Family ||
                        formData.coverIsFor === CoverIsFor.Couple
                          ? (customerLoading + partnerLoading) / 2
                          : customerLoading;

                      const cover = {
                        isCurrentHealthFund: false,
                        coverTitle: product?.product_name,
                        coverImage: getHealthFundImagePath(
                          product?.health_fund_id,
                        ),
                        coverMainPrice: product?.coverMainPrice || 0,
                        coverMainPriceDecimal: product?.coverMainPriceDecimal || 0,
                                              govRebateData: {
                        rebatePercentage: (formData.rebatePercentage * 100).toFixed(2),
                        loading: loading,
                        isExtras: formData.typeOfCover === CoverType.Extras
                      },

                        specialOfferTitle: product?.offer_headline,
                        specialOfferDescription: product?.offer_sub_headline,
                        excess: product?.excess,
                        coPayment: product?.copayment
                          ? product?.copayment === 'No'
                            ? product?.copayment
                            : formatCurrency(product?.copayment)
                          : 'No',
                        benefits: 'benefitResponse.data',
                        description: product?.description || '',
                        brochureUrl: product?.brochure_urls || '',
                        productId: product?.product_id,
                        healthFundId: product?.health_fund_id,
                        lhcComponent: product?.lhcComponent,
                        ambulance: product?.ambulance,
                      };

                      return (
                        <div
                          key={product.product_id} // Use a unique identifier here
                          className="flex md:w-full w-1/2 flex-shrink">
                          <CoverComparison
                            {...cover}
                            benefits={product?.productBenefits || []}
                            expandedBenefits={expandedBenefits}
                            onToggleDescription={handleToggleDescription}
                            productApiData={product}
                          />
                        </div>
                      );
                    })}
                  {showCurrentHealthFund &&
                    !isLoading &&
                    products.length > 0 && (
                      <CurrentHealthFund
                        expandedBenefits={expandedBenefits}
                        onToggleDescription={handleToggleDescription}
                      />
                    )}
                </div>

                {/* {showCurrentHealthFund && (
                  <div className="w-full">
                    <div className="bg-[#B8DBF7] rounded-r-[10px] rounded-tl-[10px] w-[min-content] h-[30px] truncate px-[15px] py-[7px]"></div>

                    <div className="flex md:w-full flex-shrink flex-col border border-[#D1D7DF] h-full items-center w-full">
                      <span className="text-[14px]">My current policy</span>
                      <div className="border border-dashed border-[#A2AFBE] w-[80%] h-[60px] rounded-[8px]"></div>
                    </div>
                  </div>
                )} */}
              </div>
            )}
            <div className="p-[15px] w-full flex flex-col items-start bg-[#F8F8F8] gap-[10px]">
              <span className="text-[14px] font-bold">Legend</span>
              <div className="flex items-center flex-wrap gap-[20px]">
                {' '}
                <div className="flex items-center gap-[5px]">
                  <GreenCheckIcon /> <span>Covered</span>
                </div>
                <div className="flex items-center gap-[5px]">
                  <RedCrossIcon /> <span>Excluded</span>
                </div>
                <div className="flex items-center gap-[5px]">
                  <RestrictedIcon /> <span>Restricted</span>
                </div>
                <div className="flex items-center gap-[5px]">
                  <PreConditionIcon /> <span>Pre-existing condition</span>
                </div>
              </div>
              <div
                className="flex items-center gap-[5px] cursor-pointer"
                onClick={() => {
                  setExpandLegend((prev) => !prev);
                }}>
                <span className="text-[14px] font-bold">
                  {expandLegend ? 'Show less' : 'Show more'}
                </span>
                <BottomChevron
                  className={`transition-transform duration-300 ${
                    expandLegend ? 'rotate-180' : ''
                  }`}
                />
              </div>
              {expandLegend && (
                <div className="flex flex-col gap-[15px]">
                  <div className="flex items-start gap-[5px]">
                    <GreenCheckIcon />
                    <p className="text-start text-[12px] w-full">
                      Hospital Inclusion: Services marked with a tick are included
                      in your policy, meaning your health fund will cover up to 100%
                      of the Medicare Schedule Fee for these procedures, provided
                      you've served any applicable waiting periods. However, you
                      may still face out-of-pocket fees if your surgeon or
                      anesthetist charges above the covered amount, or if you
                      choose a non-agreement hospital. To avoid surprises, always
                      contact your health fund before booking surgery to confirm
                      any potential out-of-pocket costs.
                    </p>
                  </div>
                  <div className="flex items-start gap-[5px]">
                    <RedCrossIcon />
                    <p className="text-start text-[12px] w-full">
                      Hospital Exclusion: Conditions or services which your health
                      insurance policy does not include, meaning that your insurer
                      will not pay benefits towards hospital or medical costs for
                      these items. If you choose to proceed as a private patient
                      for an excluded service, you will have very large out of
                      pocket expenses. If the services are eligible under
                      Medicare, you can still receive treatment as a public
                      patient – however, public hospital waiting lists will apply
                      and you should discuss this option with your doctor.
                    </p>
                  </div>
                  <div className="flex items-start gap-[5px]">
                    <RestrictedIcon />
                    <p className="text-start text-[12px] w-full">
                      Hospital Restriction: Condition or services which an
                      insurance policy covers to a limited extent and will pay
                      reduced benefits on hospital admissions. It is not
                      sufficient to cover the cost of a private room in a public
                      hospital or any room in a private hospital. If you are
                      admitted to a private hospital for treatment that is
                      restricted by your policy, large out of pocket expenses
                      will apply. You will have to pay the full theatre fees and
                      other costs as well as the difference for accommodation
                      fees; in some cases theatre fees can exceed the cost of
                      accommodation. If the services are eligible under
                      Medicare, you can still receive treatment as a public
                      patient – however, public hospital waiting lists will
                      apply and you should discuss this option with your doctor.
                    </p>
                  </div>

                  <div className="flex items-start gap-[5px]">
                    <PreConditionIcon />
                    <p className="text-start text-[12px] w-full">
                      Pre-existing condition: A pre-existing condition is an
                      ailment, illness or condition, the signs or symptoms of
                      which, in the opinion of a medical practitioner appointed
                      by the health insurer, existed at any time during the six
                      months prior to taking out hospital cover or upgrading to
                      a higher level of cover. Health insurers are able to
                      impose a maximum 12 month waiting period for hospital
                      treatment for ailments, illnesses or conditions that are
                      considered to be pre-existing. For psychiatric care,
                      rehabilitation and palliative care, the maximum waiting
                      period is two months, even if the condition is
                      pre-existing. If you are going to hospital during your
                      waiting period, it is important to check with your health
                      insurer prior to the admission as to whether you will be
                      covered or if the condition will be deemed pre-existing.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col items-start gap-[10px] mt-[10px] w-full md:w-5/6">
              <span className="text-[14px] font-bold">Disclaimer</span>
              <p className="text-left font-bold">How Our Rankings work</p>
              <p className="text-left">
                The "Top 10 Most Popular Hospital," "Top 10 Most Popular Extras,"
                "Top 10 Most Popular Combined," and "Sort by Popularity" options
                are based on the policies most commonly chosen by our customers
                through our consultant team. These rankings are updated every six
                months and do not include policies purchased directly through our
                website. This approach helps to avoid any unfair bias towards
                policies that simply appear higher up in the search results.
              </p>
              <p className="text-left font-bold">
                Important Information to Keep in Mind
              </p>
              <p className="text-left">
                If you switch hospital cover, you generally will not need to
                re-serve any hospital waiting periods you have already completed,
                provided you are switching to an equivalent or lower level of
                cover. However, if you change your extras cover, any claims you
                have already made during the current health fund year may be
                deducted from your new annual limits.
              </p>
              <p className="text-left ">
                Health Deal does not represent every health fund available in
                Australia, and we may not display every policy from the funds we
                do partner with. Our goal is to help you find better value health
                cover.
              </p>
              <p className="text-left">
                We have designed our website to make comparing health insurance as
                simple and helpful as possible for everyday Australians, but there
                are limits to what can be shown online. If you need any assistance
                or have questions, we encourage you to speak with one of our
                friendly consultants who are happy to help.
              </p>
            </div>
          </div>
          <Modal
            opened={opened}
            onClose={() => {
              close();
            }}
            size={613}
            fullScreen
            centered
            title="Filter"
            transitionProps={{ transition: 'fade', duration: 200 }}
            className="relative flex flex-col">
            <Tabs color="teal" defaultValue="first">
              <Tabs.List>
                <Tabs.Tab value="first" color="#C72A16">
                  Hospital Cover
                </Tabs.Tab>
                <Tabs.Tab value="second" color="#C72A16">
                  Extras Cover
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="first" pt="xs">
                <div className="flex flex-col items-start">
                  <div className="grid grid-cols-1 gap-4 mb-[30px]">
                    {(showMoreHospital
                      ? hospitalCoverFeatures
                      : hospitalCoverFeatures.slice(0, 14)
                    ).map((benefit: any) => (
                      <div
                        key={benefit.benefit_id} // Use unique key
                        className="flex items-center gap-[10px]">
                        <Checkbox
                          color="#17385D"
                          checked={formData.selectedHospitalBenefits.includes(
                            benefit.benefit_id,
                          )}
                          onChange={() => handleHospitalBenefitChange(benefit.benefit_id)}
                        />
                        <span className="text-[16px] flex items-center">
                          <div className="w-[30px]">
                            {getHospitalFeatureIcon(benefit.benefit_name)}
                          </div>
                          {benefit.benefit_name}
                        </span>
                      </div>
                    ))}
                  </div>
                  <span
                    className="text-center cursor-pointer self-center mb-[40px]"
                    onClick={() => setShowMoreHospital(!showMoreHospital)}>
                    {showMoreHospital ? 'Show less -' : 'Show more +'}
                  </span>
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="second" pt="xs">
                <div className="flex flex-col items-start">
                  <div className="grid grid-cols-1 gap-4 mb-[30px]">
                    {(showMoreExtras
                      ? extrasCoverFeatures
                      : extrasCoverFeatures.slice(0, 14)
                    ).map((benefit: any) => (
                      <div
                        key={benefit.benefit_id} // Use unique key
                        className="flex items-center gap-[10px]">
                        <Checkbox
                          color="#17385D"
                          checked={formData.selectedExtrasBenefits.includes(
                            benefit.benefit_id,
                          )}
                          onChange={() => handleExtrasBenefitChange(benefit.benefit_id)}
                        />
                        <span className="text-[16px] flex items-center">
                          <div className="w-[30px]">
                            {getExtrasFeatureIcon(benefit.benefit_name)}
                          </div>
                          {benefit.benefit_name}
                        </span>
                      </div>
                    ))}
                  </div>
                  <span
                    className="text-center cursor-pointer self-center mb-[40px]"
                    onClick={() => setShowMoreExtras(!showMoreExtras)}>
                    {showMoreExtras ? 'Show less -' : 'Show more +'}
                  </span>
                </div>
              </Tabs.Panel>
            </Tabs>

            <div className="sticky bg-[white] w-full bottom-0 bg-white mt-[30px] py-[15px] border-t shadow-md flex items-center justify-between">
              <span className="text-[16px] font-bold cursor-pointer ml-[34px] underline truncate">
                Clear filters
              </span>
              <PrimaryButton
                onClick={() => {
                  handleSaveFilters();
                  close();
                }}
                text="View results"
                style={{ whiteSpace: 'nowrap' }}
              />
            </div>
          </Modal>
        </div>
        {compareProducts.length > 0 && (
          <div className="sticky bottom-0 bg-[#F3F9FE] gap-[10px] p-4 shadow-md border-t border-[#17385D] flex flex-col items-start">
            <span className="text-[13px] text-center self-center">
              Tick the 'Compare' checkbox to compare policies. You can select up
              to 4 items
            </span>
            <div className="flex gap-[10px] w-full">
              {compareProducts[0] ? (
                <div className="w-full flex relative items-center justify-center h-[48px]">
                  <img
                    src={compareProducts[0].coverImage}
                    className="h-[30px]"
                  />
                  <div
                    className="absolute top-[10px] right-0 cursor-pointer"
                    onClick={() => {
                      dispatch(
                        removeCompareProduct(compareProducts[0].productId),
                      );
                    }}>
                    <TrashIcon />
                  </div>
                </div>
              ) : (
                <div className="w-full flex relative items-center justify-center border border-dashed border-[#A2AFBE] rounded-[8px] h-[48px] ">
                  <span className="text-[14px] text-[#45607D]">Add 3 More</span>
                </div>
              )}
              {compareProducts[1] ? (
                <div className="w-full flex relative items-center justify-center h-[48px]">
                  <img
                    src={compareProducts[1].coverImage}
                    className="h-[30px]"
                  />
                  <div
                    className="absolute top-[10px] right-0 cursor-pointer"
                    onClick={() => {
                      dispatch(
                        removeCompareProduct(compareProducts[1].productId),
                      );
                    }}>
                    <TrashIcon />
                  </div>
                </div>
              ) : (
                <div className="w-full flex relative items-center justify-center border border-dashed border-[#A2AFBE] rounded-[8px] h-[48px] ">
                  <span className="text-[14px] text-[#45607D]">Add 3 More</span>
                </div>
              )}
              {compareProducts[2] ? (
                <div className="w-full flex relative items-center justify-center h-[48px]">
                  <img
                    src={compareProducts[2].coverImage}
                    className="h-[30px]"
                  />
                  <div
                    className="absolute top-[10px] right-0 cursor-pointer"
                    onClick={() => {
                      dispatch(
                        removeCompareProduct(compareProducts[2].productId),
                      );
                    }}>
                    <TrashIcon />
                  </div>
                </div>
              ) : (
                <div className="w-full flex relative items-center justify-center border border-dashed border-[#A2AFBE] rounded-[8px] h-[48px] ">
                  {compareProducts[1] && (
                    <span className="text-[14px] text-[#45607D]">Add 2 More</span>
                  )}
                </div>
              )}
              {compareProducts[3] ? (
                <div className="w-full flex relative items-center justify-center h-[48px]">
                  <img
                    src={compareProducts[3].coverImage}
                    className="h-[30px]"
                  />
                  <div
                    className="absolute top-[10px] right-0 cursor-pointer"
                    onClick={() => {
                      dispatch(
                        removeCompareProduct(compareProducts[3].productId),
                      );
                    }}>
                    <TrashIcon />
                  </div>
                </div>
              ) : (
                <div className="w-full flex relative items-center justify-center border border-dashed border-[#A2AFBE] rounded-[8px] h-[48px] ">
                  {compareProducts[2] && (
                    <span className="text-[14px] text-[#45607D]">Add 1 More</span>
                  )}
                </div>
              )}
            </div>
            <div className="w-full flex justify-between gap-[20px] items-center">
              <span
                className="text-[16px] underline cursor-pointer"
                onClick={() => {
                  dispatch(removeAllCompareProducts());
                }}>
                Clear
              </span>
              <PrimaryButton
                text="Compare"
                classNames="w-full"
                onClick={() => {
                  if (
                    compareProducts.length < 2 ||
                    compareProducts.length > 4
                  ) {
                    toast.error(
                      'Please select between 2 and 4 products to compare',
                    );
                    return;
                  }
                  openComapre();
                }}
              />
            </div>
          </div>
        )}
        <Modal
          opened={compareOpened}
          onClose={closeCompare}
          title="Product Comparison"
          className="!w-full"
          classNames={{
            inner: 'w-full',
            content: 'w-full',
            overlay: 'w-full',
            header: 'w-full',
          }}
          fullScreen>
          <ProductComparison />
        </Modal>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div key="desktop-step8" className="h-full w-full bg-[white]">
        <div className="h-[43px] px-[17px] py-[10px] flex items-center justify-between">
          <div
            onClick={() => {
              dispatch(setCurrentStep(8));
            }}
            className="cursor-pointer flex items-center w-min">
            <LeftChevron />
            <span className="text-[12px] font-medium">Back</span>
          </div>
          <span className="text-[15px] font-bold text-[#17385D] text-center flex-1 px-[10px]">
            Trusted for 10+ Years | No Re-Serving Wait Periods | 40,000+ Aussies Found a Better Deal
          </span>
          <StartOver /> 
        </div>
        <div className="px-[15px] md:px-[55px] pb-[20px] flex flex-col">

          {/* Consolidated dark blue filter bar */}
          <div className="bg-[#17385D] rounded-[12px] px-[16px] py-[10px] flex items-center gap-[14px] mb-[8px] overflow-x-auto">
            {/* Hide/Show Filter button */}
            <div
              onClick={() => {
                setShowFilters((prev) => !prev);
              }}
              style={{ backgroundColor: '#FFFFFF', color: '#17385D' }}
              className="flex-shrink-0 flex cursor-pointer items-center font-bold justify-center gap-[6px] h-[38px] rounded-[100px] px-[16px] whitespace-nowrap">
              <div
                className={`transition-transform duration-300 ${
                  !showFilters ? 'rotate-180' : ''
                }`}>
                <LeftChevron />
              </div>
              <span className="text-[14px] font-medium">
                {showFilters ? 'Hide Filter' : 'Show Filter'}
              </span>
              <FilterIcon fillColor="#17385D" />
            </div>

            {/* Previous answers (shortened, inline) */}
            <div
              style={{ backgroundColor: '#FFFFFF' }}
              className="flex-shrink-0 flex items-center gap-[10px] rounded-[8px] px-[14px] py-[8px] whitespace-nowrap">
              <span style={{ color: '#17385D' }} className="text-[13px]">
                Prices and covers are based on your info
              </span>
              <div
                className="flex items-center gap-[4px] cursor-pointer"
                onClick={() => {
                  setExpandUserDetails((prev) => !expandUserDetails);
                }}>
                <span style={{ color: '#17385D' }} className="text-[13px] font-bold underline cursor-pointer">
                  {expandUserDetails ? 'Hide' : 'View'}
                </span>
                <BottomChevron
                  className={`transition-transform duration-300 ${
                    expandUserDetails ? 'rotate-180' : ''
                  }`}
                />
              </div>
              <span
                className="text-[13px] cursor-pointer text-[#D74F58] underline"
                onClick={() => {
                  dispatch(setCurrentStep(8));
                }}>
                Change your answers
              </span>
            </div>

            {/* Compare my current policy */}
            <div
              style={{ backgroundColor: '#FFFFFF' }}
              className="flex-shrink-0 flex items-center gap-[10px] rounded-[8px] px-[14px] py-[8px] whitespace-nowrap">
              <span style={{ color: '#17385D' }} className="text-[13px] font-medium">
                Compare my current policy
              </span>
              <Switch
                checked={showCurrentHealthFund}
                onChange={(event) =>
                  setShowCurrentHealthFund(event.currentTarget.checked)
                }
                color="dark"
                size="xs"
              />
            </div>

            {/* Order by */}
            <div className="flex-shrink-0 flex items-center gap-[8px] whitespace-nowrap ml-auto">
              <span style={{ color: '#FFFFFF' }} className="text-[13px] font-medium">Order by</span>
              <Select
                value={order}
                data={['Price', 'Popularity']}
                onChange={(value) => {
                  setOrder(value as 'Price' | 'Popularity');
                }}
                classNames={{
                  wrapper: '!w-[110px]',
                }}
                styles={{ input: { backgroundColor: '#FFFFFF', fontSize: '13px' } }}
              />
            </div>

            {/* Hospital excess */}
            <div className="flex-shrink-0 flex items-center gap-[8px] whitespace-nowrap">
              <span style={{ color: '#FFFFFF' }} className="text-[13px] font-medium">Hospital excess</span>
              <Select
                value={excessSelection}
                data={['$750', '$500', '$250']}
                onChange={(value: any) => {
                  let excess = {
                    from: 0,
                    to: 750,
                  };
                  if (value === '$750') {
                    excess = {
                      from: 750,
                      to: 750,
                    };
                  } else if (value === '$500') {
                    excess = {
                      from: 500,
                      to: 500,
                    };
                  } else if (value === '$250') {
                    excess = {
                      from: 250,
                      to: 250,
                    };
                  } else {
                    excess = {
                      from: 0,
                      to: 750,
                    };
                  }
                  setExcessSelection(value);
                }}
                classNames={{
                  wrapper: '!w-[90px]',
                }}
                styles={{ input: { backgroundColor: '#FFFFFF', fontSize: '13px' } }}
              />
            </div>
          </div>

          {/* Expanded user details (shown below filter bar when toggled) */}
          <div
            className={`text-[12px] text-start transition-all duration-300 ease-in-out overflow-hidden ${expandUserDetails ? 'max-h-[100px] opacity-100 py-[8px]' : 'max-h-0 opacity-0'}`}>
            <p>
              <strong>{formData.coverIsFor}</strong> cover in{' '}
              <strong>{formData.location}</strong>, I'm{' '}
              <strong>{calculateAge(formData.dateOfBirth[0])}</strong> years
              old,{' '}
              {[CoverIsFor.Couple, CoverIsFor.Family].includes(
                formData.coverIsFor,
              ) && formData.dateOfBirth?.[1] && formData.dateOfBirth[1] !== '-' && (
                <span>
                  my partner is{' '}
                  <strong>{calculateAge(formData.dateOfBirth[1])}</strong>
                  years old,{' '}
                </span>
              )}
              {salesReqBody.dependants.length > 0 &&
                [CoverIsFor.Family, CoverIsFor.SingleParentFamily].includes(
                  formData.coverIsFor,
                ) && (
                  <span>
                    {' '}
                    <strong>
                      {salesReqBody.dependants.length}
                    </strong> child.{' '}
                  </span>
                )}
              <strong>{(formData.rebatePercentage * 100).toFixed(2)}%</strong>{' '}
              Government Rebate. <strong>{formData.customerLoading}%</strong>{' '}
              loading.
            </p>
          </div>

          {/* Compare current policy expanded section */}
          <div
            className={`p-[15px] gap-[10px] bg-white text-[12px] w-full flex flex-col text-start transition-all duration-300 ease-in-out overflow-hidden rounded-[8px] border border-[#D1D7DF] ${showCurrentHealthFund ? 'max-h-auto opacity-100 mt-[8px]' : 'max-h-0 opacity-0 border-0 p-0'}`}>
            <span className="text-[12px] text-start">
              Select the provider and the policy to compare
            </span>
            <div className="flex gap-[15px] items-center">
              <Select
                label="Select your health fund"
                withAsterisk
                placeholder="Select from the dropdown list"
                withCheckIcon={false}
                className="w-full text-start"
                classNames={{
                  wrapper: '!h-[40px]',
                }}
                rightSection={<BottomChevron />}
                value={formData.healthFund}
                onChange={(value) => {
                  dispatch(
                    updateFormData({
                      ...formData,
                      healthFund: value,
                      hasHealthFund: true,
                    }),
                  );
                }}
                data={healthFunds}
              />
              <TextInput
                label="Select a price range"
                value={formData.previousPayment}
                onChange={(e) => {
                  dispatch(
                    updateFormData({
                      ...formData,
                      previousPayment: e.currentTarget.value,
                    }),
                  );
                }}
                withAsterisk
                placeholder="Select a price range"
                className="w-full text-start"
                classNames={{
                  wrapper: '!h-[40px]',
                  input: '!h-[40px]',
                }}
              />
              <Select
                label="Select a policy"
                placeholder="Select a policy"
                withAsterisk
                withCheckIcon={false}
                className="w-full text-start"
                classNames={{
                  wrapper: '!h-[40px]',
                }}
                rightSection={<BottomChevron />}
                data={compareHealthFundProducts.map((product: any) => ({
                  label: product.product_name,
                  value: product.product_id.toString(),
                }))}
                value={selectedProduct?.product_id.toString()}
                onChange={(value, option) => {
                  dispatch(
                    setSelectedProduct(
                      compareHealthFundProducts.find(
                        (p: any) => p.product_id == value,
                      ),
                    ),
                  );
                }}
              />
            </div>
          </div>
          {/* Toggle Section */}

          <div
            ref={container}
            className={`w-[100px] h-[100px] self-center  ${
              isLoading ? 'block' : 'hidden'
            }`}
          />

          <div className="flex gap-[10px] items-start w-full">
            {showFilters && (
              <div className="flex flex-col w-1/4 ">
                <div className=" h-[30px] rounded-r-[10px] rounded-tl-[10px] w-[min-content] truncate px-[15px] py-[7px]"></div>
                <div className="flex flex-col w-full border border-[#D1D7DF] p-[15px] gap-[20px]">
                  <div className="flex justify-between items-center">
                    <h1 className="text-[24px] font-bold text-[#17385D] !mb-0">
                      Filter
                    </h1>
                    <span className="underline cursor-pointer">
                      Clear filters
                    </span>
                  </div>
                  <div className="flex flex-col items-start">
                    <div className="w-full flex pb-[10px] mb-[10px] justify-between items-center border-b border-[#D9D9D9]">
                      <h2 className="text-[16px] font-bold !mb-0">
                        Hospital cover
                      </h2>
                      <div
                        className={`flex items-center gap-[5px] cursor-pointer transition-transform duration-300 ${
                          expandHospital ? 'rotate-180' : 'rotate-0'
                        }`}
                        onClick={() => setExpandHospital((prev) => !prev)}>
                        <BottomChevron />
                      </div>
                    </div>

                    <div
                      className={`grid grid-cols-1 gap-4 mb-[30px] overflow-hidden transition-all duration-300 ${
                        expandHospital
                          ? 'max-h-[auto] opacity-100'
                          : 'max-h-0 opacity-0'
                      }`}>
                      {(showMoreHospital
                        ? hospitalCoverFeatures
                        : hospitalCoverFeatures.slice(0, 14)
                      ).map((benefit: any) => (
                        <Checkbox
                          key={benefit.benefit_id}
                          className="items-start justify-start flex"
                          label={
                            <span className="text-[16px] flex items-center text-start">
                              <div className="w-[20px] flex-shrink-0">
                                {getHospitalFeatureIcon(benefit.benefit_name)}
                              </div>
                              {benefit.benefit_name}
                            </span>
                          }
                          color="#17385D"
                          checked={formData.selectedHospitalBenefits.includes(
                            benefit.benefit_id,
                          )}
                          onChange={() => handleHospitalBenefitChange(benefit.benefit_id)}
                        />
                      ))}
                    </div>

                    {expandHospital && (
                      <span
                        className="text-center cursor-pointer self-center mb-[40px] transition-opacity duration-300"
                        onClick={() => setShowMoreHospital(!showMoreHospital)}>
                        {showMoreHospital ? 'Show less -' : 'Show more +'}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col items-start">
                    <div className="w-full flex pb-[10px] mb-[10px] justify-between items-center border-b border-[#D9D9D9]">
                      <h2 className="text-[16px] font-bold !mb-0">
                        Extras cover
                      </h2>
                      <div
                        className={`flex items-center gap-[5px] cursor-pointer transition-transform duration-300 ${
                          expandExtras ? 'rotate-180' : 'rotate-0'
                        }`}
                        onClick={() => setExpandExtras((prev) => !prev)}>
                        <BottomChevron />
                      </div>
                    </div>

                    <div
                      className={`grid grid-cols-1 gap-4 mb-[30px] overflow-hidden transition-all duration-300 ${
                        expandExtras
                          ? 'max-h-[auto] opacity-100'
                          : 'max-h-0 opacity-0'
                      }`}>
                      {(showMoreExtras
                        ? extrasCoverFeatures
                        : extrasCoverFeatures.slice(0, 14)
                      ).map((benefit: any) => (
                        <Checkbox
                          key={benefit.benefit_id}
                          className="text-start"
                          color="#17385D"
                          label={
                            <span className="text-[16px] flex items-center text-start">
                              <div className="w-[20px] flex-shrink-0">
                                {getExtrasFeatureIcon(benefit.benefit_name)}
                              </div>
                              {benefit.benefit_name}
                            </span>
                          }
                          checked={formData.selectedExtrasBenefits.includes(
                            benefit.benefit_id,
                          )}
                          onChange={() => handleExtrasBenefitChange(benefit.benefit_id)}
                        />
                      ))}
                    </div>

                    {expandExtras && (
                      <span
                        className="text-center cursor-pointer self-center mb-[40px] transition-opacity duration-300"
                        onClick={() => setShowMoreExtras(!showMoreExtras)}>
                        {showMoreExtras ? 'Show less -' : 'Show more +'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {isLoading || isBenefitsLoading ? (
              <div className="self-start w-full items-center flex justify-center flex-col gap-[10px]">
                <span>
                  We're finding you a great Health Deal. Results are currently
                  loading, please wait up to 30 seconds.
                </span>

                {isDesktopView ? (
                  <div className="flex w-full md:gap-[20px] gap-[5px]">
                    <IsLoadingCoverComparison />
                    <IsLoadingCoverComparison />
                    <IsLoadingCoverComparison />
                    {!showFilters && <IsLoadingCoverComparison />}
                  </div>
                ) : (
                  <div className="flex w-full md:gap-[20px] gap-[5px]">
                    <IsLoadingCoverComparison />
                    <IsLoadingCoverComparison />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex w-full relative gap-[5px] justify-between items-start">
                {products && (
                  <div
                    className={`rounded-[6px] z-[100] absolute top-[60px] left-[-10px] p-[6px] ${
                      pageNumber === 1
                        ? 'cursor-not-allowed bg-[#D1D7DF]'
                        : 'cursor-pointer bg-[#17385D]'
                    }`}
                    onClick={handlePrev}>
                    <LeftChevronButton fillColor="white" />
                  </div>
                )}
                {products && (
                  <div
                    className="rounded-[6px] bg-[#17385D] z-[100] absolute top-[80px] right-[-10px] p-[6px] cursor-pointer"
                    onClick={handleNext}>
                    <RightChevronButton fillColor="white" />
                  </div>
                )}
                {products && products.length === 0 && (
                  <div className="flex items-center  justify-center w-full">
                    <span className="text-[16px] font-medium">
                      There are no items matching your filters
                    </span>
                  </div>
                )}
                {products &&
                  products.map((product: any, index: number) => {
                    if (isMobileView && index > 1) return null;

                    const customerLoading = Number.parseInt(
                      formData.customerLoading,
                    );
                    const partnerLoading = Number.parseInt(
                      formData.partnerLoading,
                    );
                    const loading = (
                      (formData.coverIsFor === CoverIsFor.Family ||
                       formData.coverIsFor === CoverIsFor.Couple) &&
                      formData.dateOfBirth?.[1] && 
                      formData.dateOfBirth[1] !== '-'
                    ) ? (customerLoading + partnerLoading) / 2 : customerLoading;

                    const cover = {
                      isCurrentHealthFund: false,
                      coverTitle: product?.product_name,
                      coverImage: getHealthFundImagePath(
                        product?.health_fund_id,
                      ),
                      coverMainPrice: product?.coverMainPrice || 0,
                      coverMainPriceDecimal: product?.coverMainPriceDecimal || 0,
                                              govRebateData: {
                        rebatePercentage: (formData.rebatePercentage * 100).toFixed(2),
                        loading: loading,
                        isExtras: formData.typeOfCover === CoverType.Extras
                      },

                        specialOfferTitle: product?.offer_headline,
                        specialOfferDescription: product?.offer_sub_headline,
                        excess: product?.excess,
                        coPayment: product?.copayment
                          ? product?.copayment === 'No'
                            ? product?.copayment
                            : formatCurrency(product?.copayment)
                          : 'No',
                        benefits: 'benefitResponse.data',
                        description: product?.description || '',
                        brochureUrl: product?.brochure_urls || '',
                        productId: product?.product_id,
                        healthFundId: product?.health_fund_id,
                        lhcComponent: product?.lhcComponent,
                        ambulance: product?.ambulance,
                      };

                    if (showCurrentHealthFund) {
                      if (index === 2) {
                        return (
                          <CurrentHealthFund
                            expandedBenefits={expandedBenefits}
                            onToggleDescription={handleToggleDescription}
                          />
                        );
                      }
                    }

                    return (
                      <div
                        key={product.product_id} // Use a unique identifier here
                        className="flex md:w-full w-1/2 flex-shrink"
                        style={{}}>
                        <CoverComparison
                          {...cover}
                          benefits={product?.productBenefits || []}
                          expandedBenefits={expandedBenefits}
                          onToggleDescription={handleToggleDescription}
                          productApiData={product}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
         
          </div>
          <div className="p-[15px] w-full flex flex-col items-start bg-[#F8F8F8] gap-[10px]">
            <span className="text-[14px] font-bold">Legend</span>
            <div className="flex items-center flex-wrap gap-[20px]">
              {' '}
              <div className="flex items-center gap-[5px]">
                <GreenCheckIcon /> <span>Covered</span>
              </div>
              <div className="flex items-center gap-[5px]">
                <RedCrossIcon /> <span>Excluded</span>
              </div>
              <div className="flex items-center gap-[5px]">
                <RestrictedIcon /> <span>Restricted</span>
              </div>
              <div className="flex items-center gap-[5px]">
                <PreConditionIcon /> <span>Pre-existing condition</span>
              </div>
            </div>
            <div
              className="flex items-center gap-[5px] cursor-pointer"
              onClick={() => {
                setExpandLegend((prev) => !prev);
              }}>
              <span className="text-[14px] font-bold">
                {expandLegend ? 'Show less' : 'Show more'}
              </span>
              <BottomChevron
                className={`transition-transform duration-300 ${
                  expandLegend ? 'rotate-180' : ''
                }`}
              />
            </div>
            {expandLegend && (
              <div className="flex flex-col gap-[15px]">
                <div className="flex items-start gap-[5px]">
                  <GreenCheckIcon />
                  <p className="text-start text-[12px] w-full">
                    Hospital Inclusion: Services marked with a tick are included in your policy, meaning your health fund will cover up to 100%
                    of the Medicare Schedule Fee for these procedures, provided
                    you've served any applicable waiting periods. However, you
                    may still face out-of-pocket fees if your surgeon or
                    anesthetist charges above the covered amount, or if you
                    choose a non-agreement hospital. To avoid surprises, always
                    contact your health fund before booking surgery to confirm
                    any potential out-of-pocket costs.
                  </p>
                </div>
                <div className="flex items-start gap-[5px]">
                  <RedCrossIcon />
                  <p className="text-start text-[12px] w-full">
                    Hospital Exclusion: Conditions or services which your health
                    insurance policy does not include, meaning that your insurer
                    will not pay benefits towards hospital or medical costs for
                    these items. If you choose to proceed as a private patient
                    for an excluded service, you will have very large out of
                    pocket expenses. If the services are eligible under
                    Medicare, you can still receive treatment as a public
                    patient – however, public hospital waiting lists will apply
                    and you should discuss this option with your doctor.
                  </p>
                </div>
                <div className="flex items-start gap-[5px]">
                  <RestrictedIcon />
                  <p className="text-start text-[12px] w-full">
                    Hospital Restriction: Condition or services which an
                    insurance policy covers to a limited extent and will pay
                    reduced benefits on hospital admissions. It is not
                    sufficient to cover the cost of a private room in a public
                    hospital or any room in a private hospital. If you are
                    admitted to a private hospital for treatment that is
                    restricted by your policy, large out of pocket expenses will
                    apply. You will have to pay the full theatre fees and other
                    costs as well as the difference for accommodation fees; in
                    some cases theatre fees can exceed the cost of
                    accommodation. If the services are eligible under Medicare,
                    you can still receive treatment as a public patient –
                    however, public hospital waiting lists will apply and you
                    should discuss this option with your doctor.
                  </p>
                </div>

                <div className="flex items-start gap-[5px]">
                  <PreConditionIcon />
                  <p className="text-start text-[12px] w-full">
                    Pre-existing condition: A pre-existing condition is an
                    ailment, illness or condition, the signs or symptoms of
                    which, in the opinion of a medical practitioner appointed
                    by the health insurer, existed at any time during the six
                    months prior to taking out hospital cover or upgrading to
                    a higher level of cover. Health insurers are able to
                    impose a maximum 12 month waiting period for hospital
                    treatment for ailments, illnesses or conditions that are
                    considered to be pre-existing. For psychiatric care,
                    rehabilitation and palliative care, the maximum waiting
                    period is two months, even if the condition is
                    pre-existing. If you are going to hospital during your
                    waiting period, it is important to check with your health
                    insurer prior to the admission as to whether you will be
                    covered or if the condition will be deemed pre-existing.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col items-start gap-[10px] mt-[10px] w-full md:w-5/6">
            <span className="text-[14px] font-bold">Disclaimer</span>
            <p className="text-left font-bold">How Our Rankings work</p>
            <p className="text-left">
              The "Top 10 Most Popular Hospital," "Top 10 Most Popular Extras,"
              "Top 10 Most Popular Combined," and "Sort by Popularity" options
              are based on the policies most commonly chosen by our customers
              through our consultant team. These rankings are updated every six
              months and do not include policies purchased directly through our
              website. This approach helps to avoid any unfair bias towards
              policies that simply appear higher up in the search results.
            </p>
            <p className="text-left font-bold">
              Important Information to Keep in Mind
            </p>
            <p className="text-left">
              If you switch hospital cover, you generally will not need to
              re-serve any hospital waiting periods you have already completed,
              provided you are switching to an equivalent or lower level of
              cover. However, if you change your extras cover, any claims you
              have already made during the current health fund year may be
              deducted from your new annual limits.
            </p>
            <p className="text-left ">
              Health Deal does not represent every health fund available in
              Australia, and we may not display every policy from the funds we
              do partner with. Our goal is to help you find better value health
              cover.
            </p>
            <p className="text-left">
              We have designed our website to make comparing health insurance as
              simple and helpful as possible for everyday Australians, but there
              are limits to what can be shown online. If you need any assistance
              or have questions, we encourage you to speak with one of our
              friendly consultants who are happy to help.
            </p>
          </div>
        </div>
        <Modal
          opened={opened}
          onClose={() => {
            close();
          }}
          size={613}
          fullScreen
          centered
          title="Filter"
          transitionProps={{ transition: 'fade', duration: 200 }}
          className="relative flex flex-col">
          <Tabs color="teal" defaultValue="first">
            <Tabs.List>
              <Tabs.Tab value="first" color="#C72A16">
                Hospital Cover
              </Tabs.Tab>
              <Tabs.Tab value="second" color="#C72A16">
                Extras Cover
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="first" pt="xs">
              <div className="flex flex-col items-start">
                <div className="grid grid-cols-1 gap-4 mb-[30px]">
                  {(showMoreHospital
                    ? hospitalCoverFeatures
                    : hospitalCoverFeatures.slice(0, 14)
                  ).map((benefit: any) => (
                    <div
                      key={benefit.benefit_id}
                      className="flex items-center gap-[10px]">
                      <Checkbox
                        color="#17385D"
                        checked={formData.selectedHospitalBenefits.includes(
                          benefit.benefit_id,
                        )}
                        onChange={() => handleHospitalBenefitChange(benefit.benefit_id)}
                      />
                      <span className="text-[16px] flex items-center">
                        <div className="w-[30px]">
                          {getHospitalFeatureIcon(benefit.benefit_name)}
                        </div>
                        {benefit.benefit_name}
                      </span>
                    </div>
                  ))}
                </div>
                <span
                  className="text-center cursor-pointer self-center mb-[40px]"
                  onClick={() => setShowMoreHospital(!showMoreHospital)}>
                  {showMoreHospital ? 'Show less -' : 'Show more +'}
                </span>
              </div>
            </Tabs.Panel>

            <Tabs.Panel value="second" pt="xs">
              <div className="flex flex-col items-start">
                <div className="grid grid-cols-1 gap-4 mb-[30px]">
                  {(showMoreExtras
                    ? extrasCoverFeatures
                    : extrasCoverFeatures.slice(0, 14)
                  ).map((benefit: any) => (
                    <div
                      key={benefit.benefit_id}
                      className="flex items-center gap-[10px]">
                      <Checkbox
                        color="#17385D"
                        checked={formData.selectedExtrasBenefits.includes(
                          benefit.benefit_id,
                        )}
                        onChange={() => handleExtrasBenefitChange(benefit.benefit_id)}
                      />
                      <span className="text-[16px] flex items-center">
                        <div className="w-[30px]">
                          {getExtrasFeatureIcon(benefit.benefit_name)}
                        </div>
                        {benefit.benefit_name}
                      </span>
                    </div>
                  ))}
                </div>
                <span
                  className="text-center cursor-pointer self-center mb-[40px]"
                  onClick={() => setShowMoreExtras(!showMoreExtras)}>
                  {showMoreExtras ? 'Show less -' : 'Show more +'}
                </span>
              </div>
            </Tabs.Panel>
          </Tabs>

          <div className="sticky bg-[white] w-full bottom-0 bg-white mt-[30px] py-[15px] border-t shadow-md flex items-center justify-between">
            <span className="text-[16px] font-bold cursor-pointer ml-[34px] underline truncate">
              Clear filters
            </span>
            <PrimaryButton
              onClick={() => {
                handleSaveFilters();
                close();
              }}
              text="View results"
              style={{ whiteSpace: 'nowrap' }}
            />
          </div>
        </Modal>
      </div>
      {compareProducts.length > 0 && (
        <div className="sticky bottom-0 bg-[#F3F9FE] gap-[10px] p-4 shadow-md border-t border-[#17385D] flex flex-col items-start">
          <span className="text-[13px] text-center self-center">
            Tick the 'Compare' checkbox to compare policies. You can select up
            to 4 items
          </span>
          <div className="flex gap-[10px] w-full">
            {compareProducts[0] ? (
              <div className="w-full flex relative items-center justify-center h-[48px]">
                <img
                  src={compareProducts[0].coverImage}
                  className="h-[30px]"
                />
                <div
                  className="absolute top-[10px] right-0 cursor-pointer"
                  onClick={() => {
                    dispatch(
                      removeCompareProduct(compareProducts[0].productId),
                    );
                  }}>
                  <TrashIcon />
                </div>
              </div>
            ) : (
              <div className="w-full flex relative items-center justify-center border border-dashed border-[#A2AFBE] rounded-[8px] h-[48px] ">
                <span className="text-[14px] text-[#45607D]">Add 3 More</span>
              </div>
            )}
            {compareProducts[1] ? (
              <div className="w-full flex relative items-center justify-center h-[48px]">
                <img
                  src={compareProducts[1].coverImage}
                  className="h-[30px]"
                />
                <div
                  className="absolute top-[10px] right-0 cursor-pointer"
                  onClick={() => {
                    dispatch(
                      removeCompareProduct(compareProducts[1].productId),
                    );
                  }}>
                    <TrashIcon />
                  </div>
              </div>
            ) : (
              <div className="w-full flex relative items-center justify-center border border-dashed border-[#A2AFBE] rounded-[8px] h-[48px] ">
                <span className="text-[14px] text-[#45607D]">Add 3 More</span>
              </div>
            )}
            {compareProducts[2] ? (
              <div className="w-full flex relative items-center justify-center h-[48px]">
                <img
                  src={compareProducts[2].coverImage}
                  className="h-[30px]"
                />
                <div
                  className="absolute top-[10px] right-0 cursor-pointer"
                  onClick={() => {
                    dispatch(
                      removeCompareProduct(compareProducts[2].productId),
                    );
                  }}>
                    <TrashIcon />
                  </div>
              </div>
            ) : (
              <div className="w-full flex relative items-center justify-center border border-dashed border-[#A2AFBE] rounded-[8px] h-[48px] ">
                {compareProducts[1] && (
                  <span className="text-[14px] text-[#45607D]">Add 2 More</span>
                )}
              </div>
            )}
            {compareProducts[3] ? (
              <div className="w-full flex relative items-center justify-center h-[48px]">
                <img
                  src={compareProducts[3].coverImage}
                  className="h-[30px]"
                />
                <div
                  className="absolute top-[10px] right-0 cursor-pointer"
                  onClick={() => {
                    dispatch(
                      removeCompareProduct(compareProducts[3].productId),
                    );
                  }}>
                    <TrashIcon />
                  </div>
              </div>
            ) : (
              <div className="w-full flex relative items-center justify-center border border-dashed border-[#A2AFBE] rounded-[8px] h-[48px] ">
                {compareProducts[2] && (
                  <span className="text-[14px] text-[#45607D]">Add 1 More</span>
                )}
              </div>
            )}
          </div>
          <div className="w-full flex justify-between gap-[20px] items-center">
            <span
              className="text-[16px] underline cursor-pointer"
              onClick={() => {
                dispatch(removeAllCompareProducts());
              }}>
              Clear
            </span>
            <PrimaryButton
              text="Compare"
              classNames="w-full"
              onClick={() => {
                if (
                  compareProducts.length < 2 ||
                  compareProducts.length > 4
                ) {
                  toast.error(
                    'Please select between 2 and 4 products to compare',
                  );
                  return;
                }
                openComapre();
              }}
            />
          </div>
        </div>
      )}
      <Modal
        opened={compareOpened}
        onClose={closeCompare}
        title="Product Comparison"
        fullScreen>
        <ProductComparison />
      </Modal>
    </BaseLayout>
  );
}

export default Step8;